import React, { Fragment, useEffect, useState } from "react";
import { Card, Col, Row, Table, Form, Button, Spinner } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";
import { useSelector } from "react-redux";
import PermissionWrapper from "../PermissionWrapper";

const JMRList = () => {
 const user = useSelector((state) => state.auth?.userDetails || {});
 const location = useLocation();
 const navigate = useNavigate();
 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [projectId, setProjectId] = useState(() => {
 const params = new URLSearchParams(location.search);
 return params.get("project") || "";
 });
 const [workOrderId, setWorkOrderId] = useState(() => {
 const params = new URLSearchParams(location.search);
 return params.get("workOrder") || "";
 });
 const [status, setStatus] = useState(() => {
 const params = new URLSearchParams(location.search);
 return params.get("status") || "";
 });
 const [fromDate, setFromDate] = useState(() => {
 const params = new URLSearchParams(location.search);
 return params.get("fromDate") || "";
 });
 const [toDate, setToDate] = useState(() => {
 const params = new URLSearchParams(location.search);
 return params.get("toDate") || "";
 });
 const [sheets, setSheets] = useState([]);
 const [loading, setLoading] = useState(false);
 const [exporting, setExporting] = useState(false);
 const [exportingPdf, setExportingPdf] = useState(false);

 useEffect(() => {
 const params = new URLSearchParams(location.search);
 const project = params.get("project") || "";
 const workOrder = params.get("workOrder") || "";
 const from = params.get("fromDate") || "";
 const to = params.get("toDate") || "";
 const stat = params.get("status") || "";
 setProjectId(project);
 setWorkOrderId(workOrder);
 setFromDate(from);
 setToDate(to);
 setStatus(stat);
 }, [location.search]);

 const buildUrlParams = (updates = {}) => {
 const params = new URLSearchParams();
 const pId = updates.project !== undefined ? updates.project : projectId;
const wId = updates.workOrder !== undefined ? updates.workOrder : workOrderId;
 const stat = updates.status !== undefined ? updates.status : status;
 const from = updates.fromDate !== undefined ? updates.fromDate : fromDate;
 const to = updates.toDate !== undefined ? updates.toDate : toDate;

 if (pId) params.set("project", pId);
 if (wId) params.set("workOrder", wId);
 if (stat) params.set("status", stat);
 if (from) params.set("fromDate", from);
 if (to) params.set("toDate", to);

 return params.toString();
 };

  const handleProjectSelect = (val) => {
    const updates = { project: val };
    if (val && workOrderId) {
      const currentWo = workOrders.find((w) => String(w._id) === String(workOrderId));
      const currentWoProjId = currentWo?.project?._id || currentWo?.project;
      if (currentWoProjId && String(currentWoProjId) !== String(val)) {
        updates.workOrder = "";
      }
    }
    const queryString = buildUrlParams(updates);
    navigate(queryString ? `/jmr?${queryString}` : "/jmr");
  };

  const handleWorkOrderSelect = (val) => {
    const updates = { workOrder: val };
    if (val) {
      const selectedWo = workOrders.find((w) => String(w._id) === String(val));
      const autoProjId = selectedWo?.project?._id || selectedWo?.project;
      if (autoProjId) {
        updates.project = String(autoProjId);
      }
    }
    const queryString = buildUrlParams(updates);
    navigate(queryString ? `/jmr?${queryString}` : "/jmr");
  };

  const updateFilter = (filterName, value) => {
    const updates = { [filterName]: value };
    const queryString = buildUrlParams(updates);
    navigate(queryString ? `/jmr?${queryString}` : "/jmr");
  };

 const buildParams = () => {
 const params = {};
 if (projectId) params.projectId = projectId;
 if (workOrderId) params.workOrderId = workOrderId;
 if (status) params.status = status;
 if (fromDate) params.fromDate = fromDate;
 if (toDate) params.toDate = toDate;
 return params;
 };

 useEffect(() => {
 const loadProjects = async () => {
 try {
 const res = await api.get("/projects");
 if (res.data.success) {
 setProjects(res.data.data || []);
 }
 } catch (error) {
 console.error(error);
 }
 };

 loadProjects();
 }, []);

 useEffect(() => {
 const loadWorkOrders = async () => {
 try {
 if (projectId) {
 const res = await api.get(`/reconciliation/by-project/${projectId}`);
 if (res.data.success) {
 setWorkOrders(res.data.data || []);
 }
 return;
 }

 const res = await api.get("/work-orders?noLimit=true");
 if (res.data.success) {
 setWorkOrders(res.data.data || []);
 }
 } catch (error) {
 console.error(error);
 }
 };

 loadWorkOrders();
 }, [projectId]);

 const fetchSheets = async () => {
 try {
 setLoading(true);
 const res = await api.get("/measurement-sheets/summary", {
 params: buildParams(),
 });
 if (res.data.success) {
  const validSheets = (res.data.data || []).filter(
    (sheet) => sheet.project && (sheet.project._id || sheet.project.projectName)
  );
  setSheets(validSheets);
 } else {
 setSheets([]);
 }
 } catch (error) {
 setSheets([]);
 swal("Error", error.response?.data?.message || error.message, "error");
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 fetchSheets();
 }, [projectId, workOrderId, status, fromDate, toDate]);

 const downloadFile = (blob, fileName) => {
 const url = window.URL.createObjectURL(blob);
 const link = document.createElement("a");
 link.href = url;
 link.download = fileName;
 document.body.appendChild(link);
 link.click();
 link.remove();
 window.setTimeout(() => window.URL.revokeObjectURL(url), 3000);
 };

 const handleExport = async () => {
 try {
 setExporting(true);
 const res = await api.get("/measurement-sheets/summary/export", {
 params: buildParams(),
 responseType: "blob",
 });
 downloadFile(res.data, "measurement-sheet-summary.xlsx");
 } catch (error) {
 swal("Error", error.response?.data?.message || error.message, "error");
 } finally {
 setExporting(false);
 }
 };

 const handleExportPdf = async () => {
 try {
 setExportingPdf(true);
 const res = await api.get("/measurement-sheets/summary/export-pdf", {
 params: buildParams(),
 responseType: "blob",
 });
 downloadFile(res.data, "measurement-sheet-summary.pdf");
 } catch (error) {
 swal("Error", error.response?.data?.message || error.message, "error");
 } finally {
 setExportingPdf(false);
 }
 };

 return (
 <Fragment>
 <PageTitle activeMenu="JMR Summary" motherMenu="ITC" />

 <Row>
 <Col lg={12}>
 <Card className="shadow-sm">
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title className="mb-0">Measurement Sheet Summary By BOQ</Card.Title>
 <div className="d-flex flex-wrap gap-2 justify-content-end">
 <PermissionWrapper user={user} required={["read:measurement-sheets"]}>
 <Button
 variant="outline-success"
 size="sm"
 onClick={handleExport}
 disabled={exporting || loading}
 >
 {exporting ? "Exporting Excel..." : "Export Excel"}
 </Button>
 </PermissionWrapper>
 <PermissionWrapper user={user} required={["read:measurement-sheets"]}>
 <Button
 variant="outline-danger"
 size="sm"
 onClick={handleExportPdf}
 disabled={exportingPdf || loading}
 >
 {exportingPdf ? "Exporting PDF..." : "Export PDF"}
 </Button>
 </PermissionWrapper>
 <PermissionWrapper user={user} required={["create:measurement-sheets"]}>
 <Link
 to={
 workOrderId
 ? `/measurement-sheets/add?${projectId ? `project=${projectId}&` : ""}workOrder=${workOrderId}`
 : "/measurement-sheets/add"
 }
 className="btn btn-primary btn-sm"
 >
 + Add Measurement Sheet
 </Link>
 </PermissionWrapper>
 </div>
 </Card.Header>
 <Card.Body>
 <div className="border-bottom pb-3 mb-3">
 <h6 className="mb-3">
 <strong>Filters</strong>
 </h6>
 <Row className="g-3">
 <Col md={3}>
 <Form.Group>
 <Form.Label className="mb-2 fw-bold">Project</Form.Label>
 <Form.Select
 size="sm"
 value={projectId}
                      onChange={(e) => handleProjectSelect(e.target.value)}
 >
 <option value="">Select Project</option>
 {projects.map((project) => (
 <option key={project._id} value={project._id}>
 {project.projectName}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={3}>
 <Form.Group>
 <Form.Label className="mb-2 fw-bold">Work Order</Form.Label>
 <Form.Select
 size="sm"
 value={workOrderId}
                      onChange={(e) => handleWorkOrderSelect(e.target.value)}
 >
 <option value="">Select Work Order</option>
 {workOrders.map((workOrder) => (
 <option key={workOrder._id} value={workOrder._id}>
                        {workOrder.woNo}{workOrder.label ? ` – ${workOrder.label}` : ""}{!projectId && (workOrder.project?.projectName || workOrder.project?.name) ? ` (${workOrder.project.projectName || workOrder.project.name})` : ""}
 </option>
 ))}
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group>
 <Form.Label className="mb-2 fw-bold">Status</Form.Label>
 <Form.Select size="sm" value={status} onChange={(e) => updateFilter("status", e.target.value)}>
 <option value="">All Statuses</option>
 <option value="Draft">Draft</option>
 <option value="Submitted">Submitted</option>
 <option value="Approved">Approved</option>
 <option value="Rejected">Rejected</option>
 </Form.Select>
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group>
 <Form.Label className="mb-2 fw-bold">From Date</Form.Label>
 <Form.Control
 size="sm"
 type="date"
 value={fromDate}
 onChange={(e) => updateFilter("fromDate", e.target.value)}
 />
 </Form.Group>
 </Col>
 <Col md={2}>
 <Form.Group>
 <Form.Label className="mb-2 fw-bold">To Date</Form.Label>
 <Form.Control
 size="sm"
 type="date"
 value={toDate}
 onChange={(e) => updateFilter("toDate", e.target.value)}
 />
 </Form.Group>
 </Col>
 </Row>
 </div>

 {loading ? (
 <div className="text-center py-5">
 <Spinner animation="border" role="status" className="text-primary" />
 <div className="text-muted mt-2">Loading measurement sheets…</div>
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover className="align-middle" size="sm" bordered>
 <thead className="table-light">
 <tr>
 <th className="text-nowrap" style={{ width: "70px" }}>#</th>
 <th className="text-nowrap" style={{ width: "160px" }}>Work Order</th>
 <th className="text-nowrap" style={{ width: "110px" }}>WLIN</th>
 <th style={{ minWidth: "240px" }}>BOQ Item</th>
 <th style={{ minWidth: "260px" }}>Description</th>
 <th className="text-nowrap" style={{ width: "90px" }}>UOM</th>
 <th className="text-nowrap" style={{ width: "120px" }}>Approved</th>
 <th className="text-nowrap" style={{ width: "120px" }}>Installed</th>
 <th className="text-nowrap" style={{ width: "120px" }}>Balance</th>
 <th className="text-nowrap" style={{ width: "120px" }}>Sheet Count</th>
 <th style={{ minWidth: "180px" }}>Sheet Types</th>
 <th style={{ minWidth: "160px" }}>Status</th>
 <th className="text-nowrap" style={{ width: "150px" }}>Latest Date</th>
 </tr>
 </thead>
 <tbody>
 {sheets.length ? (
 sheets.map((row, index) => (
 <tr key={row.boqKey}>
 <td>{index + 1}</td>
 <td>{row.woNo || "-"}</td>
 <td>{row.wlino}</td>
 <td>{row.itemName}</td>
 <td className="text-truncate" style={{ maxWidth: 320 }} title={row.description}>
 {row.description}
 </td>
 <td>{row.unit}</td>
 <td>{Number(row.approvedQty || 0).toFixed(2)}</td>
 <td>{Number(row.installedQty || 0).toFixed(2)}</td>
 <td>{Number(row.balanceQty || 0).toFixed(2)}</td>
 <td>{row.sheetCount}</td>
 <td>{row.types || "-"}</td>
 <td>{row.statuses}</td>
 <td>
 {row.latestDate
 ? new Date(row.latestDate).toLocaleDateString("en-IN")
 : "-"}
 </td>
 </tr>
 ))
 ) : (
 <tr>
 <td colSpan={13} className="text-center py-4 text-muted">
 No measurement sheet summary found
 </td>
 </tr>
 )}
 </tbody>
 </Table>
 </div>
 )}

 {!loading && (
 <div className="d-flex justify-content-end mt-3">
 <PermissionWrapper user={user} required={["read:measurement-sheets"]}>
 <Button
 variant="outline-primary"
 size="sm"
 as={Link}
 to={
 workOrderId
 ? `/measurement-sheets?${projectId ? `project=${projectId}&` : ""}workOrder=${workOrderId}`
 : "/measurement-sheets"
 }
 >
 Open Measurement Sheet Records
 </Button>
 </PermissionWrapper>
 </div>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default JMRList;
